import { useState, useEffect, useCallback } from "react";
import { Lock, Mail, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

import { motion, AnimatePresence } from "framer-motion";
const DEFAULT_FORM = {
  blocked_advance: "",
  admin_notification_email: "",
};

function inputClass(hasError) {
  return `w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-400 focus:ring-red-200"
      : "border-gray-200 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]"
  }`;
}

function validate(form) {
  const errors = {};

  if (form.blocked_advance === "" || form.blocked_advance === null) {
    errors.blocked_advance = "Advance booking period is required.";
  } else if (Number(form.blocked_advance) < 0) {
    errors.blocked_advance = "Must be 0 or greater.";
  }

  if (!form.admin_notification_email) {
    errors.admin_notification_email = "Notification email is required.";
  } else if (
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.admin_notification_email)
  ) {
    errors.admin_notification_email = "Enter a valid email address.";
  }

  return errors;
}

function Field({ label, required, icon, error, name, children }) {
  return (
    <div data-field={name}>
      <FieldLabel required={required} icon={icon}>
        {label}
      </FieldLabel>
      {children}
      {error && <FieldError message={error} />}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm"
    >
      <h2 className="text-lg font-bold mb-4 text-[#0B2421] flex items-center">
        {Icon && <Icon className="h-5 w-5 mr-2" />}
        {title}
      </h2>
      {children}
    </motion.div>
  );
}

function FieldLabel({ children, required, icon: Icon }) {
  return (
    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
      {Icon && <Icon className="inline h-4 w-4 mr-1" />}
      {children} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
  );
}

function FieldError({ message }) {
  return (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="h-3 w-3 flex-shrink-0" />
      {message}
    </p>
  );
}
export default function SettingsAdmin({ onFetch, onUpdate }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const data = await onFetch();
        if (!active) return;
        setForm({
          blocked_advance: data.blocked_advance ?? "",
          admin_notification_email: data.admin_notification_email ?? "",
        });
      } catch (err) {
        console.error("Error fetching settings:", err);
        if (active) setSaveError("Failed to load settings.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const markTouched = useCallback((name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate((prevForm) => prevForm));
  }, []);

  const fieldError = (name) => (touched[name] ? errors[name] : undefined);

  const handleSave = async () => {
    const validationErrors = validate(form);
    setErrors(validationErrors);
    setTouched({
      blocked_advance: true,
      admin_notification_email: true,
    });

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await onUpdate(form);
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading settings...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8F7] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-start justify-between pb-5">
          <div>
            <h1 className="text-2xl font-bold">Settings</h1>
            <p className="text-gray-600">
              Manage your account settings and preferences.
            </p>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="h-12 px-8 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors disabled:opacity-40 flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>

        {saveError && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-600">
            {saveError}
          </div>
        )}

        {savedAt && !saving && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-2.5 text-sm text-green-700">
            <CheckCircle2 className="w-4 h-4" />
            Settings saved successfully.
          </div>
        )}

        <Section title="Block Specific Dates" icon={Lock}>
          <div className="mb-5">
            <p className="mt-1.5 text-xs text-gray-400">
              This setting allows you to block dates for Advanced bookings. From
              the current date up to this limit, users will not be able to make
              bookings on that date. You can set the limit by entering a time in
              hours.
            </p>
          </div>

          <Field
            label="Advanced Booking Period (hours)"
            name="blocked_advance"
            error={fieldError("blocked_advance")}
          >
            <div className="relative">
              <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[#D4AF37] font-semibold">
                hours
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.blocked_advance}
                onChange={(e) => setField("blocked_advance", e.target.value)}
                onBlur={() => markTouched("blocked_advance")}
                placeholder="e.g. 24"
                className={`${inputClass(fieldError("blocked_advance"))} pl-20`}
              />
            </div>
          </Field>
        </Section>

        <div className="mt-8">
          <Section title="Booking Notifications" icon={Mail}>
            <div className="mb-5">
              <p className="mt-1.5 text-xs text-gray-400">
                New bookings will trigger a notification email to this address
                so admins can review and confirm them.
              </p>
            </div>

            <Field
              label="Admin Notification Email"
              name="admin_notification_email"
              error={fieldError("admin_notification_email")}
            >
              <input
                type="email"
                value={form.admin_notification_email}
                onChange={(e) =>
                  setField("admin_notification_email", e.target.value)
                }
                onBlur={() => markTouched("admin_notification_email")}
                placeholder="admin@exqura.com"
                className={inputClass(fieldError("admin_notification_email"))}
              />
            </Field>
          </Section>
        </div>
      </div>
    </div>
  );
}
